import React, {useEffect, useState} from 'react';
import {Card, Input, Switch, Button, Tag, message, Spin, Tooltip} from 'antd';
import {GoogleOutlined, WindowsOutlined, FacebookOutlined, AppleOutlined, LockOutlined, EyeInvisibleOutlined, EyeOutlined} from '@ant-design/icons';
import {useAuth} from '../context/auth-context';
import './admin-oidc.scss';

// /admin/oidc — Administrator-only OIDC provider configuration.
// Live writes through to the secrets table; client_secret encrypted at rest.
// New values take effect on the next /auth/<provider> call (no restart).

const PROVIDERS = [
    {key: 'google',    label: 'Google',    icon: <GoogleOutlined />,   wired: true,  hint: 'Web Application client. Authorized redirect URI must match exactly.'},
    {key: 'microsoft', label: 'Microsoft', icon: <WindowsOutlined />,  wired: false, hint: 'Azure AD app registration. Tenant-aware discovery URL not yet wired in v1.'},
    {key: 'meta',      label: 'Meta',      icon: <FacebookOutlined />, wired: false, hint: 'Meta uses OAuth 2.0 (no id_token by default). Storage only in v1.'},
    {key: 'apple',     label: 'Apple',     icon: <AppleOutlined />,    wired: false, hint: 'Apple OIDC needs team_id + key_id + private_key. Will get its own form when wired.'},
];

function AdminOidc() {
    const {user} = useAuth();
    const [configs, setConfigs] = useState(null);
    const [error, setError] = useState(null);
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState({});
    const [reveal, setReveal] = useState({});

    useEffect(() => {
        if (user.role !== 'Administrator') return;
        fetch('/api/admin/oidc')
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((d) => setConfigs(d.providers))
            .catch((err) => setError(err.message));
    }, [user.role]);

    if (user.role !== 'Administrator') {
        return <div className="portal-admin-oidc__forbidden">403 — Administrator role required.</div>;
    }
    if (error) return <div className="portal-admin-oidc__error">Error loading configs: {error}</div>;
    if (!configs) return <div className="portal-admin-oidc__loading"><Spin /></div>;

    function stage(provider, patch) {
        setEdits((prev) => ({...prev, [provider]: {...(prev[provider] || {}), ...patch}}));
    }

    function effective(provider) {
        return {...configs[provider], ...(edits[provider] || {})};
    }

    function isDirty(provider) {
        const e = edits[provider];
        if (!e) return false;
        const cur = configs[provider];
        for (const k of Object.keys(e)) {
            if (k === 'client_secret') {
                // Empty string in edit means "no change"; only actual content is dirty.
                if (e.client_secret !== '' && e.client_secret !== undefined) return true;
                continue;
            }
            if (e[k] !== cur[k]) return true;
        }
        return false;
    }

    async function save(provider) {
        const e = edits[provider] || {};
        const patch = {};
        for (const k of Object.keys(e)) {
            if (k === 'client_secret') {
                if (e.client_secret === '' || e.client_secret === undefined) continue;
                patch.client_secret = e.client_secret;
            } else if (e[k] !== configs[provider][k]) {
                patch[k] = e[k];
            }
        }
        if (Object.keys(patch).length === 0) return;
        setSaving((s) => ({...s, [provider]: true}));
        try {
            const r = await fetch(`/api/admin/oidc/${provider}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(patch),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            setConfigs((prev) => ({...prev, [provider]: d.config}));
            setEdits((prev) => {
                const next = {...prev};
                delete next[provider];
                return next;
            });
            setReveal((prev) => ({...prev, [provider]: false}));
            message.success(`${provider} OIDC config saved`);
        } catch (err) {
            message.error(`Save failed: ${err.message}`);
        } finally {
            setSaving((s) => {
                const next = {...s};
                delete next[provider];
                return next;
            });
        }
    }

    return (
        <div className="portal-admin-oidc">
            <h1 className="portal-admin-oidc__title">OIDC providers</h1>
            <p className="portal-admin-oidc__subtitle">
                Edit live. Saved values are stored in the <code>secrets</code> table — <code>client_secret</code> encrypted at rest with the
                server&rsquo;s master key. New values take effect on the next <code>/auth/&lt;provider&gt;</code> call (no server restart needed).
                <code> .env</code> values are used as fallback when DB rows are absent.
            </p>

            {PROVIDERS.map(({key, label, icon, wired, hint}) => {
                const eff = effective(key);
                const dirty = isDirty(key);
                const revealed = reveal[key];
                return (
                    <Card
                        key={key}
                        className="portal-admin-oidc__card"
                        title={
                            <span className="portal-admin-oidc__provider-head">
                                {icon}
                                <strong>{label}</strong>
                                {wired
                                    ? <Tag color={eff.enabled ? 'green' : 'default'}>{eff.enabled ? 'enabled' : 'disabled'}</Tag>
                                    : <Tooltip title="Storage only — auth flow not wired in v1"><Tag color="default">flow not wired</Tag></Tooltip>
                                }
                            </span>
                        }
                        extra={
                            <Button
                                type="primary"
                                onClick={() => save(key)}
                                disabled={!dirty}
                                loading={Boolean(saving[key])}
                            >
                                Save
                            </Button>
                        }
                    >
                        <div className="portal-admin-oidc__row">
                            <label>Enabled</label>
                            <Switch
                                checked={Boolean(eff.enabled)}
                                onChange={(checked) => stage(key, {enabled: checked})}
                            />
                            <span className="portal-admin-oidc__hint">{hint}</span>
                        </div>

                        <div className="portal-admin-oidc__row">
                            <label>Client ID</label>
                            <Input
                                value={eff.client_id || ''}
                                onChange={(e) => stage(key, {client_id: e.target.value})}
                                placeholder="from provider console"
                            />
                        </div>

                        <div className="portal-admin-oidc__row">
                            <label>Client secret</label>
                            <Input.Password
                                value={(edits[key] && edits[key].client_secret !== undefined) ? edits[key].client_secret : ''}
                                onChange={(e) => stage(key, {client_secret: e.target.value})}
                                placeholder={configs[key].client_secret_set ? '•••••••• (saved — leave empty to keep)' : 'set client secret'}
                                visibilityToggle={{visible: revealed, onVisibleChange: (v) => setReveal((prev) => ({...prev, [key]: v}))}}
                                iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                                prefix={<LockOutlined />}
                            />
                            <span className="portal-admin-oidc__hint">
                                {configs[key].client_secret_set ? 'A secret is on file — typing here replaces it. Leave empty to keep.' : 'No secret stored yet.'}
                            </span>
                        </div>

                        <div className="portal-admin-oidc__row">
                            <label>Redirect URI</label>
                            <Input
                                value={eff.redirect_uri || ''}
                                onChange={(e) => stage(key, {redirect_uri: e.target.value})}
                                placeholder="http://localhost:3001/auth/callback"
                            />
                        </div>
                    </Card>
                );
            })}
        </div>
    );
}

export default AdminOidc;
